import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type {
  Workflow,
  WorkflowNode,
  WorkflowEdge,
  HistoryEntry,
  NodeType,
  WorkflowNodeData,
} from '../types/workflow';

// 历史记录管理
interface HistoryState {
  entries: HistoryEntry[];
  currentIndex: number;
}

// 工作流状态
interface WorkflowState {
  // 当前工作流
  currentWorkflow: Workflow | null;
  // 工作流列表
  workflows: Workflow[];
  // 选中节点
  selectedNode: WorkflowNode | null;
  // 历史记录
  history: HistoryState;
  // 加载状态
  isLoading: boolean;
  // 错误信息
  error: string | null;
}

// 工作流操作
interface WorkflowActions {
  // 工作流CRUD
  setCurrentWorkflow: (workflow: Workflow | null) => void;
  setWorkflows: (workflows: Workflow[]) => void;
  createWorkflow: (name: string, description?: string) => Workflow;
  updateWorkflow: (workflowId: string, updates: Partial<Workflow>) => void;
  deleteWorkflow: (workflowId: string) => void;

  // 节点操作
  addNode: (type: NodeType, position: { x: number; y: number }, data?: Partial<WorkflowNodeData>) => void;
  updateNode: (nodeId: string, data: Partial<WorkflowNodeData>) => void;
  removeNode: (nodeId: string) => void;
  setSelectedNode: (node: WorkflowNode | null) => void;

  // 边操作
  addEdge: (edge: WorkflowEdge) => void;
  removeEdge: (edgeId: string) => void;
  updateEdge: (edgeId: string, updates: Partial<WorkflowEdge>) => void;

  // 历史记录
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  saveToHistory: () => void;

  // 状态管理
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
}

// 创建默认节点数据
const createDefaultNodeData = (type: NodeType): WorkflowNodeData => {
  const baseLabel = getDefaultLabel(type);

  switch (type) {
    case 'start':
      return {
        label: baseLabel,
        type: 'start',
        initialVariables: {},
      };
    case 'ai':
      return {
        label: baseLabel,
        type: 'ai',
        prompt: '',
        model: 'gpt-3.5-turbo',
        temperature: 0.7,
        maxTokens: 2048,
        outputVariable: 'result',
      };
    case 'condition':
      return {
        label: baseLabel,
        type: 'condition',
        condition: '',
        trueLabel: '是',
        falseLabel: '否',
      };
    case 'api':
      return {
        label: baseLabel,
        type: 'api',
        method: 'GET',
        url: '',
        headers: {},
        timeout: 30000,
        retryCount: 0,
        outputVariable: 'response',
      };
    case 'end':
      return {
        label: baseLabel,
        type: 'end',
        outputVariables: {},
      };
    default:
      return {
        label: baseLabel,
        type: 'start',
        initialVariables: {},
      };
  }
};

const getDefaultLabel = (type: NodeType): string => {
  const labels: Record<NodeType, string> = {
    start: '开始',
    ai: 'AI处理',
    condition: '条件判断',
    api: 'API调用',
    end: '结束',
  };
  return labels[type];
};

// 生成唯一ID
const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// 创建初始历史记录
const createInitialHistory = (nodes: WorkflowNode[], edges: WorkflowEdge[]): HistoryState => ({
  entries: [{ nodes: [...nodes], edges: [...edges], timestamp: Date.now() }],
  currentIndex: 0,
});

// 创建store
export const useWorkflowStore = create<WorkflowState & WorkflowActions>()(
  devtools(
    persist(
      (set, get) => ({
        // 初始状态
        currentWorkflow: null,
        workflows: [],
        selectedNode: null,
        history: { entries: [], currentIndex: -1 },
        isLoading: false,
        error: null,

        // 设置当前工作流
        setCurrentWorkflow: (workflow) => {
          set({
            currentWorkflow: workflow,
            selectedNode: null,
            history: workflow
              ? createInitialHistory(workflow.nodes, workflow.edges)
              : { entries: [], currentIndex: -1 },
          });
        },

        // 设置工作流列表
        setWorkflows: (workflows) => set({ workflows }),

        // 创建工作流
        createWorkflow: (name, description) => {
          const startNodeData = createDefaultNodeData('start');
          const startNode: WorkflowNode = {
            id: 'start-1',
            type: 'start',
            position: { x: 250, y: 50 },
            data: startNodeData,
          };

          const workflow: Workflow = {
            id: generateId(),
            name,
            description,
            nodes: [startNode],
            edges: [],
            createdAt: Date.now(),
            updatedAt: Date.now(),
            version: 1,
            isActive: true,
          };

          set((state) => ({
            workflows: [...state.workflows, workflow],
            currentWorkflow: workflow,
            selectedNode: null,
            history: createInitialHistory([startNode], []),
          }));

          return workflow;
        },

        // 更新工作流
        updateWorkflow: (workflowId, updates) => {
          set((state) => {
            const workflows = state.workflows.map((w) =>
              w.id === workflowId ? { ...w, ...updates, updatedAt: Date.now() } : w
            );

            const currentWorkflow =
              state.currentWorkflow?.id === workflowId
                ? { ...state.currentWorkflow, ...updates, updatedAt: Date.now() }
                : state.currentWorkflow;

            return { workflows, currentWorkflow };
          });
        },

        // 删除工作流
        deleteWorkflow: (workflowId) => {
          set((state) => ({
            workflows: state.workflows.filter((w) => w.id !== workflowId),
            currentWorkflow:
              state.currentWorkflow?.id === workflowId ? null : state.currentWorkflow,
          }));
        },

        // 添加节点
        addNode: (type, position, data) => {
          const state = get();
          if (!state.currentWorkflow) return;

          const defaultData = createDefaultNodeData(type);
          const newNode: WorkflowNode = {
            id: generateId(),
            type,
            position,
            data: { ...defaultData, ...data } as WorkflowNodeData,
          };

          const updatedNodes = [...state.currentWorkflow.nodes, newNode];
          const updatedWorkflow: Workflow = {
            ...state.currentWorkflow,
            nodes: updatedNodes,
            updatedAt: Date.now(),
          };

          set({
            currentWorkflow: updatedWorkflow,
            selectedNode: newNode,
          });

          get().saveToHistory();
        },

        // 更新节点
        updateNode: (nodeId, data) => {
          const state = get();
          if (!state.currentWorkflow) return;

          const updatedNodes = state.currentWorkflow.nodes.map((node) =>
            node.id === nodeId ? { ...node, data: { ...node.data, ...data } as WorkflowNodeData } : node
          );

          const updatedWorkflow: Workflow = {
            ...state.currentWorkflow,
            nodes: updatedNodes,
            updatedAt: Date.now(),
          };

          const updatedSelectedNode =
            state.selectedNode?.id === nodeId
              ? { ...state.selectedNode, data: { ...state.selectedNode.data, ...data } as WorkflowNodeData }
              : state.selectedNode;

          set({
            currentWorkflow: updatedWorkflow,
            selectedNode: updatedSelectedNode as WorkflowNode | null,
          });
        },

        // 删除节点
        removeNode: (nodeId) => {
          const state = get();
          if (!state.currentWorkflow) return;

          const updatedNodes = state.currentWorkflow.nodes.filter((n) => n.id !== nodeId);
          const updatedEdges = state.currentWorkflow.edges.filter(
            (e) => e.source !== nodeId && e.target !== nodeId
          );

          const updatedWorkflow: Workflow = {
            ...state.currentWorkflow,
            nodes: updatedNodes,
            edges: updatedEdges,
            updatedAt: Date.now(),
          };

          set({
            currentWorkflow: updatedWorkflow,
            selectedNode: state.selectedNode?.id === nodeId ? null : state.selectedNode,
          });

          get().saveToHistory();
        },

        // 设置选中节点
        setSelectedNode: (node) => set({ selectedNode: node }),

        // 添加边
        addEdge: (edge) => {
          const state = get();
          if (!state.currentWorkflow) return;

          // 检查是否已存在相同的边
          const exists = state.currentWorkflow.edges.some(
            (e) => e.source === edge.source && e.target === edge.target && e.sourceHandle === edge.sourceHandle
          );
          if (exists) return;

          const updatedEdges = [...state.currentWorkflow.edges, edge];
          const updatedWorkflow: Workflow = {
            ...state.currentWorkflow,
            edges: updatedEdges,
            updatedAt: Date.now(),
          };

          set({ currentWorkflow: updatedWorkflow });
          get().saveToHistory();
        },

        // 删除边
        removeEdge: (edgeId) => {
          const state = get();
          if (!state.currentWorkflow) return;

          const updatedEdges = state.currentWorkflow.edges.filter((e) => e.id !== edgeId);
          const updatedWorkflow: Workflow = {
            ...state.currentWorkflow,
            edges: updatedEdges,
            updatedAt: Date.now(),
          };

          set({ currentWorkflow: updatedWorkflow });
          get().saveToHistory();
        },

        // 更新边
        updateEdge: (edgeId, updates) => {
          const state = get();
          if (!state.currentWorkflow) return;

          const updatedEdges = state.currentWorkflow.edges.map((edge) =>
            edge.id === edgeId ? { ...edge, ...updates } : edge
          );

          const updatedWorkflow: Workflow = {
            ...state.currentWorkflow,
            edges: updatedEdges,
            updatedAt: Date.now(),
          };

          set({ currentWorkflow: updatedWorkflow });
        },

        // 保存到历史记录
        saveToHistory: () => {
          const state = get();
          if (!state.currentWorkflow) return;

          const newEntry: HistoryEntry = {
            nodes: [...state.currentWorkflow.nodes],
            edges: [...state.currentWorkflow.edges],
            timestamp: Date.now(),
          };

          // 如果当前不是最新的历史记录，删除后面的记录
          const entries = state.history.entries.slice(0, state.history.currentIndex + 1);

          // 限制历史记录数量
          const maxHistory = 50;
          const newEntries = [...entries, newEntry].slice(-maxHistory);

          set({
            history: {
              entries: newEntries,
              currentIndex: newEntries.length - 1,
            },
          });
        },

        // 撤销
        undo: () => {
          const state = get();
          if (!state.canUndo() || !state.currentWorkflow) return;

          const newIndex = state.history.currentIndex - 1;
          const entry = state.history.entries[newIndex];

          set({
            currentWorkflow: {
              ...state.currentWorkflow,
              nodes: entry.nodes,
              edges: entry.edges,
              updatedAt: Date.now(),
            },
            history: {
              ...state.history,
              currentIndex: newIndex,
            },
          });
        },

        // 重做
        redo: () => {
          const state = get();
          if (!state.canRedo() || !state.currentWorkflow) return;

          const newIndex = state.history.currentIndex + 1;
          const entry = state.history.entries[newIndex];

          set({
            currentWorkflow: {
              ...state.currentWorkflow,
              nodes: entry.nodes,
              edges: entry.edges,
              updatedAt: Date.now(),
            },
            history: {
              ...state.history,
              currentIndex: newIndex,
            },
          });
        },

        // 是否可以撤销
        canUndo: () => {
          const state = get();
          return state.history.currentIndex > 0;
        },

        // 是否可以重做
        canRedo: () => {
          const state = get();
          return state.history.currentIndex < state.history.entries.length - 1;
        },

        // 设置加载状态
        setLoading: (isLoading) => set({ isLoading }),

        // 设置错误
        setError: (error) => set({ error }),

        // 清除错误
        clearError: () => set({ error: null }),
      }),
      {
        name: 'workflow-storage',
        partialize: (state) => ({
          workflows: state.workflows,
          currentWorkflow: state.currentWorkflow,
        }),
      }
    ),
    { name: 'WorkflowStore' }
  )
);

export default useWorkflowStore;
